import { Role } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';

import { FormPage } from '@/components/page/FormPage';
import { useModelCatalog } from '@/features/models/useModelCatalog';

export const Route = createFileRoute('/_baseLayout/models')({
  beforeLoad: async ({ context: { accessGuard } }) =>
    accessGuard([Role.SuperAdmin]),
  component: ModelOverview,
});

function ModelOverview() {
  const { t } = useLingui();
  const { data, locale, money, isError, refetch } = useModelCatalog();
  const groups = [
    { id: 'recommended', title: t`Recommended choices` },
    { id: 'alternatives', title: t`Premium and alternatives` },
    { id: 'experimental', title: t`Experimental` },
  ];

  return (
    <FormPage title={t`Model comparison`} breadcrumbs={[]}>
      <div className='space-y-6'>
        <p className='max-w-3xl text-muted-fg'>
          <Trans>
            Find the right balance of quality, speed and price for your
            assistant. Prices include your organization&apos;s pricing and
            currency conversion. Costs and response times are approximate and
            vary with the conversation.
          </Trans>
        </p>
        {!data && (
          <p>
            {isError ? t`Unable to load model prices.` : t`Loading models…`}
          </p>
        )}
        {isError && (
          <button type='button' onClick={() => void refetch()}>
            <Trans>Retry</Trans>
          </button>
        )}
        {groups.map(group => (
          <section key={group.id} className='space-y-3'>
            <h2 className='text-xl font-semibold'>{group.title}</h2>
            <div className='grid gap-4 lg:grid-cols-2'>
              {data?.models
                .filter(model => model.section === group.id)
                .map(model => (
                  <article
                    key={model.id}
                    className='space-y-3 rounded-xl border border-border bg-white p-5'
                  >
                    <h3 className='text-lg font-semibold'>{model.name}</h3>
                    <p>{model.summary[locale]}</p>
                    <p className='text-sm text-muted-fg'>
                      {model.detail[locale]}
                    </p>
                    <dl className='grid grid-cols-2 gap-3 text-sm'>
                      <div>
                        <dt className='text-muted-fg'>
                          <Trans>Average per answer</Trans>
                        </dt>
                        <dd className='font-semibold'>
                          {model.measurement?.mean == null
                            ? t`Price estimate unavailable`
                            : money(model.measurement.mean)}
                        </dd>
                      </div>
                      <div>
                        <dt className='text-muted-fg'>
                          <Trans>Typical price range</Trans>
                        </dt>
                        <dd>
                          {model.measurement?.low != null &&
                          model.measurement.high != null
                            ? `${money(model.measurement.low)} – ${money(model.measurement.high)}`
                            : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className='text-muted-fg'>
                          <Trans>Typical first answer text</Trans>
                        </dt>
                        <dd>
                          {model.measurement?.currentConfiguration &&
                          model.measurement.startSeconds != null
                            ? `${model.measurement.startSeconds.toFixed(1)} s`
                            : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className='text-muted-fg'>
                          <Trans>Typical complete answer</Trans>
                        </dt>
                        <dd>
                          {model.measurement?.currentConfiguration &&
                          model.measurement.completionSeconds != null
                            ? `${model.measurement.completionSeconds.toFixed(1)} s`
                            : '—'}
                        </dd>
                      </div>
                    </dl>
                  </article>
                ))}
            </div>
          </section>
        ))}
        <details className='rounded-xl border border-border p-4'>
          <summary className='cursor-pointer'>
            <Trans>Legacy models</Trans>
          </summary>
          <p className='my-3 text-sm text-muted-fg'>
            <Trans>Existing selections remain available in the picker.</Trans>
          </p>
          <ul className='grid gap-2 text-sm md:grid-cols-3'>
            {data?.models
              .filter(model => model.section === 'legacy')
              .map(model => (
                <li key={model.id}>{model.name}</li>
              ))}
          </ul>
        </details>
      </div>
    </FormPage>
  );
}
